import {
  type EquipmentTotals,
  NO_EQUIPMENT_TOTALS,
  shipDamageBonusBpsAtLevel,
} from '@kenocyber/shared';
import { DAMAGE_MILLI_PER_UNIT } from '../damage/base';
import { effectiveBaseAttackSpeed } from './attributes';
import {
  equipmentBaseAttackSpeed,
  huntShipBaseDamageMilli,
  NO_PASSIVES,
  type PassiveAttributes,
  passiveBaseAttackSpeed,
} from './passive-attributes';

/**
 * **O tiro primário de uma CONTA, resolvido uma vez e usado nos dois modos.**
 *
 * ## O problema que este ficheiro resolve (gate V28I.W)
 *
 * O irmão deste ficheiro — `account-max-health.ts` — foi escrito porque a mesma
 * nave tinha duas vidas máximas conforme o ecrã. O mundo aberto tinha a mesma
 * doença no ATAQUE, e ela não dava erro nenhum: `entry-service.ts` congelava
 * `PVP_DEFAULT_COMBAT_PROFILE.baseDamage`, que é o perfil NORMALIZADO do duelo.
 * Um duelo normaliza de propósito — é um combate justo, e `pvp/combat-profile.ts`
 * diz isso em três parágrafos. O mundo aberto copiou a forma do bilhete do duelo
 * e levou a normalização atrás.
 *
 * O resultado era uma conta com cinco componentes lendários a disparar como uma
 * conta acabada de criar e — desde que a pesca passou a ler o tiro primário — a
 * PESCAR como ela também. O mundo aberto é PvE com economia: normalizar lá não é
 * justiça, é apagar a construção do jogador.
 *
 * ## A regra de produto, numa linha
 *
 * **A nave tem uma força.** O que a caça compõe é o que o mundo aberto congela.
 * Sem conversão, sem normalização e sem uma segunda tabela de equipamento.
 *
 * ## Parcelas, e não um número composto
 *
 * A ordem canónica do `CLAUDE.md` tem quatro passos, e este módulo devolve um
 * campo por passo em vez de um dano final. A razão é que **as joias e os
 * Corações Primordiais não estão aqui**: eles viajam noutros campos do mesmo
 * perfil congelado e têm de entrar na MESMA soma que estas percentagens.
 *
 *     dano = base_milli x (10 000 + p_conta + p_joias + corações) / 10 000
 *              + ataque_bónus
 *
 * Devolver um dano já composto obrigaria a sala a multiplicar outra vez por
 * `(1 + joias)`, o que daria `x1,09 x 1,25` onde a lei aditiva do contrato
 * V28D.2 pede `x1,34`. É também a diferença para o irmão: em `account-max-health`
 * o Coração do Titã **está** na soma, e por isso lá sai um número só.
 *
 * ## A cadência é uma TAXA aqui e um INTERVALO lá
 *
 * A caça conta ataques por segundo; o duelo e o mundo aberto contam milli-tiques
 * entre salvas. A conversão é uma recíproca, e ela vive em `hull/attack-speed.ts`
 * — a folha onde a lei da cadência já vive. Este módulo devolve a taxa-base e a
 * percentagem em separado pela mesma razão do dano: a percentagem das joias
 * soma-se à desta conta antes de a recíproca ser aplicada UMA vez.
 *
 * ## O que este módulo NÃO faz
 *
 * Não conhece o mundo aberto, não conhece o duelo, não abre base de dados e não
 * sabe o que é uma conta — recebe parcelas já derivadas, exatamente como o
 * irmão. Não impõe tecto de cadência nem de dano: capar aqui seria capar o que
 * um item concede, que o `CLAUDE.md` proíbe em três sítios.
 *
 * E não conhece cartas, procs, canhões-super nem invocações. Nenhum deles compõe
 * o tiro NORMAL de uma nave — todos nascem no caminho do disparo — portanto não
 * existe aqui parâmetro por onde algum deles pudesse entrar.
 */

/**
 * Tudo o que decide o tiro primário de uma conta, e nada mais.
 *
 * Parcelas já DERIVADAS: os pontos da progressão, a nave equipada e o seu nível,
 * os artefactos derivados do cofre a cada leitura e os cinco componentes. Não há
 * aqui um id de instância, uma skin, um socket nem uma conta.
 */
export interface AccountPrimaryShotSources {
  /** Os pontos de dano alocados, como a progressão os guarda. */
  readonly damagePoints: bigint;
  /** Os pontos de cadência alocados. */
  readonly attackSpeedPoints: bigint;
  /** A nave equipada: o bónus percentual do casco sai do par índice/nível. */
  readonly shipIndex: number;
  readonly shipLevel: number;
  /** Os artefactos, derivados do cofre a cada leitura. */
  readonly passives: PassiveAttributes;
  /** Os cinco componentes equipados. */
  readonly equipment: EquipmentTotals;
}

/**
 * A conta vazia: sem pontos, sem artefactos e sem equipamento, com a primeira
 * nave ao nível um.
 *
 * É o estado de uma conta acabada de criar, e o tiro dela é o tiro-base canónico
 * da nave — **não** o perfil normalizado do duelo, que foi exatamente a
 * substituição que este gate desfez.
 */
export const NO_ACCOUNT_PRIMARY_SHOT_SOURCES: AccountPrimaryShotSources = Object.freeze({
  damagePoints: 0n,
  attackSpeedPoints: 0n,
  shipIndex: 1,
  shipLevel: 1,
  passives: NO_PASSIVES,
  equipment: NO_EQUIPMENT_TOTALS,
});

/**
 * **Passo 1: o dano-base, em MILLI e exato, antes de qualquer percentagem.**
 *
 * A base da nave, mais os pontos alocados, mais o ataque PLANO dos componentes.
 * É `huntShipBaseDamageMilli` e mais nada — o adaptador que a caça já usa — em
 * vez de uma segunda expressão da mesma soma.
 *
 * `bigint` porque é assim que atravessa a fronteira e é assim que
 * `shotDamageMilli` o consome. O `number` que vem da caça é um inteiro seguro
 * por construção — a própria função afirma-o em vez de arredondar — portanto a
 * conversão não perde nada.
 */
export function accountBaseDamageMilli(sources: AccountPrimaryShotSources): bigint {
  return BigInt(huntShipBaseDamageMilli(sources.damagePoints, sources.equipment));
}

/**
 * **Passo 2: a SOMA de todas as percentagens de dano da conta, em basis points.**
 *
 * O bónus do casco pelo seu nível, os artefactos e os componentes. Somadas e
 * nunca multiplicadas em sequência: `+5 %` ao lado de `+16 %` são `+21 %`, e
 * nunca `1,05 x 1,16`.
 *
 * As joias e os Corações Primordiais não estão aqui, e a ausência é a razão de
 * este módulo devolver parcelas: eles somam-se a esta na sala, dentro do mesmo
 * parêntese.
 *
 * Tudo já é basis points e nada passa por vírgula flutuante. A caça divide por
 * cem para IMPRIMIR uma percentagem, e imprimir não é compor.
 */
export function accountDamageBps(sources: AccountPrimaryShotSources): bigint {
  return (
    BigInt(shipDamageBonusBpsAtLevel(sources.shipIndex, sources.shipLevel)) +
    sources.passives.effects.damageBps +
    sources.equipment.attackBps
  );
}

/**
 * **Passo 4: o ataque bónus, em milli, e FORA da percentagem.**
 *
 * Somado depois, nunca multiplicado por ela e nunca parte da base que ela
 * multiplica. É o único membro da família que se comporta assim, e é isso que o
 * torna igual para um principiante e para alguém a carregar `+900 %`.
 *
 * Cada projétil leva-o uma vez. Uma salva de vinte e oito tiros leva-o vinte e
 * oito vezes, porque cada tiro é um acerto com o seu próprio número.
 */
export function accountBonusAttackMilli(sources: AccountPrimaryShotSources): bigint {
  return sources.equipment.bonusAttackMilli;
}

/**
 * **Passo 1 da cadência: a taxa-base, em ataques por segundo.**
 *
 * Os pontos alocados, mais o plano dos artefactos, mais o plano dos componentes
 * — exatamente as três parcelas que `HuntSimulation.baseAttacksPerSecond` soma,
 * na mesma ordem.
 *
 * Sem tecto e sem suavização. Um Speed Fast nível 10 vale `+30,72` sozinho e o
 * valor fica honesto: o que muda é o número de projéteis DISCRETOS por segundo,
 * nunca o que se desenha (`CLAUDE.md`, "Projectiles are always discrete").
 */
export function accountBaseAttacksPerSecond(sources: AccountPrimaryShotSources): number {
  return (
    effectiveBaseAttackSpeed(sources.attackSpeedPoints) +
    passiveBaseAttackSpeed(sources.passives) +
    equipmentBaseAttackSpeed(sources.equipment)
  );
}

/**
 * **Passo 2 da cadência: a SOMA das percentagens de taxa da conta, em bps.**
 *
 * Hoje só os componentes concedem uma — os artefactos concedem taxa PLANA, e
 * entram no passo 1 — e a soma escrita assim é o que faz uma fonte futura entrar
 * sem ninguém se lembrar dela.
 *
 * Como no dano, as joias não estão aqui: o Pulso do Dragão soma-se a esta antes
 * de a recíproca ser aplicada uma vez.
 */
export function accountAttackSpeedBps(sources: AccountPrimaryShotSources): bigint {
  return sources.equipment.attackSpeedBps;
}

/**
 * **O tiro primário de uma conta: as cinco parcelas, prontas a congelar.**
 *
 * O que um limite assina num bilhete. Nenhum campo aqui é um número já composto,
 * e isso é a garantia: compor exige as joias e os corações, que viajam noutros
 * campos do mesmo perfil e entram nas mesmas somas.
 */
export interface AccountPrimaryShot {
  /** Passo 1: dano-base em milli, antes de tudo. */
  readonly baseDamageMilli: bigint;
  /** Passo 2: percentagem de dano da conta, em basis points. */
  readonly damageBps: bigint;
  /** Passo 4: ataque bónus em milli, fora da percentagem. */
  readonly bonusAttackMilli: bigint;
  /** Passo 1 da cadência: ataques por segundo, antes da percentagem. */
  readonly baseAttacksPerSecond: number;
  /** Passo 2 da cadência: percentagem de taxa da conta, em basis points. */
  readonly attackSpeedBps: bigint;
}

/** As cinco leituras de uma vez: uma chamada, uma fotografia coerente. */
export function accountPrimaryShot(sources: AccountPrimaryShotSources): AccountPrimaryShot {
  const baseDamageMilli = accountBaseDamageMilli(sources);
  if (baseDamageMilli < 1n) {
    /*
     * Sem dano não há arma. A nave-base sozinha vale mil milli e nenhuma
     * percentagem negativa existe, portanto isto marca um caminho de emissão
     * avariado e não um casco legítimo.
     */
    throw new RangeError(
      `an account hull deals at least one milli of damage, got ${baseDamageMilli}`,
    );
  }
  const baseAttacksPerSecond = accountBaseAttacksPerSecond(sources);
  if (!Number.isFinite(baseAttacksPerSecond) || baseAttacksPerSecond <= 0) {
    throw new RangeError(`an account hull fires at a positive rate, got ${baseAttacksPerSecond}`);
  }
  return {
    baseDamageMilli,
    damageBps: accountDamageBps(sources),
    bonusAttackMilli: accountBonusAttackMilli(sources),
    baseAttacksPerSecond,
    attackSpeedBps: accountAttackSpeedBps(sources),
  };
}

/**
 * O MESMO dano-base, na unidade fracionária que um painel imprime.
 *
 * Uma DERIVAÇÃO do `bigint` autoritativo e não uma segunda composição — a mesma
 * disciplina de `accountMaxHealth` ao lado de `accountMaxHealthMilli`. Nenhuma
 * aritmética de combate consome isto.
 */
export function accountBaseDamage(sources: AccountPrimaryShotSources): number {
  return Number(accountBaseDamageMilli(sources)) / DAMAGE_MILLI_PER_UNIT;
}
